import { useCallback, useEffect, useRef, useState } from 'react';
import {
  ArrowLeft,
  ArrowRight,
  Eye,
  Home,
  Image as ImageIcon,
  MessageSquareText,
  Palette,
  RectangleHorizontal,
  RectangleVertical,
  Rotate3D,
  Sparkles,
  Square,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import AnalogJoystick from '../../components/AnalogJoystick';
import NewDesignSelect from '../../components/NewDesignSelect';
import RoomRenderCaptureOverlay from '../../components/RoomRenderCaptureOverlay';
import StyledRangeSlider from '../../components/StyledRangeSlider';
import SelectedAreaThreePreview from '../../components/SelectedAreaThreePreview';
import {
  CROP_MODE_KEYS,
  CROP_MODES,
  cropConfigFromOpenAiSize,
  cropModeKeyFromOpenAiSize,
} from '../../lib/roomCropModes';
import { calculateCaptureRect } from '../../lib/roomRenderCaptureFrame';

const CROP_MODE_ICONS = {
  square: Square,
  landscape: RectangleHorizontal,
  portrait: RectangleVertical,
};

const GENERATED_IMAGE_QUALITY_OPTIONS = [
  { value: 'standard', label: 'Standard image quality', cost: 1 },
  { value: 'high', label: 'High image quality', cost: 3 },
];
const MIN_FOV = 35;
const MAX_FOV = 85;

function GenerationLoadingState() {
  return (
    <div className="generation-loading-state" role="status" aria-live="polite">
      <div className="generation-loading-card">
        <div className="generation-loading-visual" aria-hidden="true">
          <Sparkles size={42} strokeWidth={2.2} />
          <span className="generation-loading-orbit generation-loading-orbit--one" />
          <span className="generation-loading-orbit generation-loading-orbit--two" />
        </div>
        <div className="generation-loading-copy">
          <h2>Creating your realistic image</h2>
          <p>Please wait — our magic is transforming your room.</p>
        </div>
        <div className="generation-loading-status" aria-hidden="true">
          <span>Refining lighting and details</span>
          <span className="generation-loading-dot" />
          <span className="generation-loading-dot" />
          <span className="generation-loading-dot" />
        </div>
      </div>
    </div>
  );
}

/**
 * @param {{
 *   roomName: string;
 *   onSelectAnotherRoom: () => void;
 *   selectedRoomAnalysisJson: object | null;
 *   canvasWidth: number;
 *   canvasHeight: number;
 *   activeCameraPreset: object | null;
 *   cameraPresets: object[];
 *   cameraIndex: number;
 *   onCameraIndexChange: (index: number) => void;
 *   fov: number;
 *   onFovChange: (fov: number) => void;
 *   selectedAspectRatio: string;
 *   onAspectRatioChange: (openAiSize: string) => void;
 *   onCameraStateChange: (state: object) => void;
 *   joystickInputRef: React.MutableRefObject<{ x: number; y: number; active: boolean }>;
 *   captureRectRef: React.MutableRefObject<{ width: number; height: number; left: number; top: number } | null>;
 *   onRegisterCaptureActions: (actions: {
 *     captureViewportRegion: (
 *       captureRect: { width: number; height: number; left: number; top: number },
 *       outputWidth: number,
 *       outputHeight: number,
 *     ) => Promise<Blob>;
 *   } | null) => void;
 *   onCaptureLayoutChange?: () => void;
 *   roomMoveBounds: { polygon: number[][]; metersPerPixel: number; marginM?: number } | null;
 *   roomType: string;
 *   onRoomTypeChange: (value: string) => void;
 *   roomTypeOptions: string[];
 *   style: string;
 *   onStyleChange: (value: string) => void;
 *   styleOptions: string[];
 *   decorationLevel: string;
 *   onDecorationLevelChange: (value: string) => void;
 *   decorationLevelOptions: string[];
 *   themeColorSource: string;
 *   selectedColors: string[];
 *   themeColorButtonCaption: string | null;
 *   onOpenThemeColors: () => void;
 *   themeColorsPopupOpen: boolean;
 *   wallColorSource: string;
 *   selectedWallColors: string[];
 *   wallColorButtonCaption: string | null;
 *   onOpenWallColors: () => void;
 *   wallColorsPopupOpen: boolean;
 *   userNotes: string;
 *   onUserNotesChange: (value: string) => void;
 *   generatedImageQuality: string;
 *   onGeneratedImageQualityChange: (value: string) => void;
 *   generating: boolean;
 *   canGenerate: boolean;
 *   hasInsufficientCredits: boolean;
 *   onGenerate: () => void;
 * }} props
 */
function RoomRenderSetupLayout({
  roomName,
  onSelectAnotherRoom,
  selectedRoomAnalysisJson,
  canvasWidth,
  canvasHeight,
  activeCameraPreset,
  cameraPresets,
  cameraIndex,
  onCameraIndexChange,
  fov,
  onFovChange,
  selectedAspectRatio,
  onAspectRatioChange,
  onCameraStateChange,
  joystickInputRef,
  captureRectRef,
  onRegisterCaptureActions,
  onCaptureLayoutChange,
  roomMoveBounds,
  roomType,
  onRoomTypeChange,
  roomTypeOptions,
  style,
  onStyleChange,
  styleOptions,
  decorationLevel,
  onDecorationLevelChange,
  decorationLevelOptions,
  themeColorSource,
  selectedColors,
  themeColorButtonCaption,
  onOpenThemeColors,
  themeColorsPopupOpen,
  wallColorSource,
  selectedWallColors,
  wallColorButtonCaption,
  onOpenWallColors,
  wallColorsPopupOpen,
  userNotes,
  onUserNotesChange,
  generatedImageQuality,
  onGeneratedImageQualityChange,
  generating,
  canGenerate,
  hasInsufficientCredits,
  onGenerate,
}) {
  const viewportRef = useRef(null);
  const [viewportSize, setViewportSize] = useState({ width: 0, height: 0 });
  const [captureRect, setCaptureRect] = useState({ width: 0, height: 0, left: 0, top: 0 });
  const [showRotateHint, setShowRotateHint] = useState(true);

  const aspectConfig = cropConfigFromOpenAiSize(selectedAspectRatio);
  const selectedAspectKey = cropModeKeyFromOpenAiSize(selectedAspectRatio);
  const joystickEnabled = Boolean(roomMoveBounds?.polygon?.length && roomMoveBounds?.metersPerPixel);

  useEffect(() => {
    setShowRotateHint(true);
  }, [selectedRoomAnalysisJson]);

  const updateCaptureLayout = useCallback(() => {
    const node = viewportRef.current;
    if (!node) return;
    const width = node.clientWidth;
    const height = node.clientHeight;
    if (!width || !height) return;
    setViewportSize({ width, height });
    const nextRect = calculateCaptureRect(
      width,
      height,
      aspectConfig.outWidth,
      aspectConfig.outHeight,
    );
    setCaptureRect(nextRect);
    captureRectRef.current = nextRect;
    onCaptureLayoutChange?.();
  }, [aspectConfig.outHeight, aspectConfig.outWidth, captureRectRef, onCaptureLayoutChange]);

  useEffect(() => {
    updateCaptureLayout();
    const node = viewportRef.current;
    if (!node || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(() => {
      updateCaptureLayout();
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [updateCaptureLayout, selectedAspectRatio, selectedRoomAnalysisJson]);

  const handleJoystickInput = useCallback(
    (input) => {
      joystickInputRef.current = input;
    },
    [joystickInputRef],
  );

  const handlePrevView = () => {
    if (!cameraPresets.length) return;
    onCameraIndexChange((cameraIndex - 1 + cameraPresets.length) % cameraPresets.length);
  };

  const handleNextView = () => {
    if (!cameraPresets.length) return;
    onCameraIndexChange((cameraIndex + 1) % cameraPresets.length);
  };

  return (
    <section className="dashboard-grid new-design-workspace design-dual-layout new-design-workspace--preview-heading floorplan-room-render-layout">
      <article className="card new-design-setup-card floorplan-room-render-sidebar-card">
        <div className="new-design-control-group floorplan-room-render-setup-room-header">
          <p className="floorplan-room-render-sidebar-kicker">Selected room</p>
          <h2 className="new-design-subheading">{roomName || 'Selected room'}</h2>
          <button
            type="button"
            className="btn btn-outline full-width floorplan-room-render-select-another-btn"
            onClick={onSelectAnotherRoom}
          >
            Select Another Room
          </button>
        </div>

        <div className="new-design-control-group">
          <h3 className="new-design-subheading" id="floorplan-room-render-type-label">
            <Home className="new-design-subheading-icon" size={18} aria-hidden />
            Room type
          </h3>
          <NewDesignSelect
            id="room-type"
            aria-labelledby="floorplan-room-render-type-label"
            value={roomType}
            onChange={onRoomTypeChange}
            options={roomTypeOptions}
            disabled={generating}
          />
        </div>

        <div className="new-design-control-group">
          <h3 className="new-design-subheading" id="floorplan-room-render-style-label">
            <Eye className="new-design-subheading-icon" size={18} aria-hidden />
            Design style
          </h3>
          <NewDesignSelect
            id="design-style"
            aria-labelledby="floorplan-room-render-style-label"
            value={style}
            onChange={onStyleChange}
            options={styleOptions}
            className="new-design-style-select"
            disabled={generating}
          />
        </div>

        <div className="new-design-control-group">
          <h3 className="new-design-subheading" id="floorplan-room-render-decoration-label">
            <Sparkles className="new-design-subheading-icon" size={18} aria-hidden />
            Decoration level
          </h3>
          <NewDesignSelect
            id="decoration-level"
            aria-labelledby="floorplan-room-render-decoration-label"
            value={decorationLevel}
            onChange={onDecorationLevelChange}
            options={decorationLevelOptions}
            disabled={generating}
          />
        </div>

        <div className="new-design-control-group new-design-colors-combined-group">
          <h3 className="new-design-subheading new-design-colors-combined-heading">
            <Palette className="new-design-subheading-icon" size={18} aria-hidden />
            Colors
          </h3>
          <div className="new-design-colors-combined-grid">
            <div className="new-design-colors-combined-item">
              <span
                className="new-design-colors-combined-label"
                id="floorplan-room-render-theme-colors-label"
              >
                Theme
              </span>
              <button
                type="button"
                className="btn btn-ghost new-design-customize-popup-trigger new-design-customize-popup-trigger--color-picker"
                aria-haspopup="dialog"
                aria-expanded={themeColorsPopupOpen}
                aria-controls="floorplan-room-render-theme-colors-popup"
                aria-labelledby="floorplan-room-render-theme-colors-label"
                onClick={onOpenThemeColors}
              >
                {themeColorSource === 'custom' && selectedColors.length > 0 ? (
                  <span
                    className="new-design-color-swatches new-design-color-swatches--in-button"
                    aria-label="Selected custom theme colors"
                  >
                    {selectedColors.map((color) => (
                      <span
                        key={`theme-${color}`}
                        className="new-design-color-swatch"
                        style={{ backgroundColor: color }}
                      />
                    ))}
                  </span>
                ) : null}
                {themeColorButtonCaption ? (
                  <span className="new-design-customize-trigger-caption">{themeColorButtonCaption}</span>
                ) : null}
              </button>
            </div>
            <div className="new-design-colors-combined-item">
              <span
                className="new-design-colors-combined-label"
                id="floorplan-room-render-wall-colors-label"
              >
                Walls
              </span>
              <button
                type="button"
                className="btn btn-ghost new-design-customize-popup-trigger new-design-customize-popup-trigger--color-picker"
                aria-haspopup="dialog"
                aria-expanded={wallColorsPopupOpen}
                aria-controls="floorplan-room-render-wall-colors-popup"
                aria-labelledby="floorplan-room-render-wall-colors-label"
                onClick={onOpenWallColors}
              >
                {wallColorSource === 'custom' && selectedWallColors.length > 0 ? (
                  <span
                    className="new-design-color-swatches new-design-color-swatches--in-button"
                    aria-label="Selected custom wall colors"
                  >
                    {selectedWallColors.map((color) => (
                      <span
                        key={`wall-${color}`}
                        className="new-design-color-swatch"
                        style={{ backgroundColor: color }}
                      />
                    ))}
                  </span>
                ) : null}
                {wallColorButtonCaption ? (
                  <span className="new-design-customize-trigger-caption">{wallColorButtonCaption}</span>
                ) : null}
              </button>
            </div>
          </div>
        </div>

        <div className="new-design-control-group">
          <h3 className="new-design-subheading" id="floorplan-room-render-notes-label">
            <MessageSquareText className="new-design-subheading-icon" size={18} strokeWidth={2} aria-hidden />
            Notes & instructions for the AI
          </h3>
          <textarea
            id="user-notes"
            className="dashboard-notes new-design-notes"
            aria-labelledby="floorplan-room-render-notes-label"
            value={userNotes}
            onChange={(event) => onUserNotesChange(event.target.value)}
            placeholder="e.g. warm lighting, neutral palette"
            rows={3}
            disabled={generating}
          />
        </div>

        <div className="new-design-control-group new-design-setup-quality-group">
          <h3 className="new-design-subheading" id="floorplan-room-render-image-quality-label">
            <ImageIcon className="new-design-subheading-icon" size={18} strokeWidth={2} aria-hidden />
            Image quality
          </h3>
          <NewDesignSelect
            id="floorplan-room-render-image-quality-select"
            aria-labelledby="floorplan-room-render-image-quality-label"
            value={generatedImageQuality}
            onChange={onGeneratedImageQualityChange}
            options={GENERATED_IMAGE_QUALITY_OPTIONS}
            disabled={generating}
          />
        </div>

        <div className="new-design-setup-generate-group">
          <button
            type="button"
            className="btn btn-primary full-width new-design-generate-btn"
            onClick={onGenerate}
            disabled={!canGenerate || hasInsufficientCredits}
          >
            <Sparkles size={25} strokeWidth={2} aria-hidden="true" />
            {generating ? 'Generating...' : 'Generate Realistic Image'}
          </button>
          {hasInsufficientCredits ? (
            <div className="new-design-no-credits" role="status">
              <span>You do not have enough credits.</span>
              <Link to="/credits" className="btn btn-outline new-design-get-credit-btn">
                Get credit
              </Link>
            </div>
          ) : null}
          {generating ? (
            <div className="generate-waiting-indicator" role="status" aria-live="polite">
              <div className="generate-waiting-text">Generating your room preview...</div>
              <div className="generate-progress-track" aria-hidden="true">
                <div className="generate-progress-fill" />
              </div>
            </div>
          ) : null}
        </div>
      </article>

      <div
        className={`new-design-workspace-preview new-design-workspace-preview--has-image floorplan-room-render-main-card${
          generating ? ' new-design-workspace-preview--submitting' : ''
        }`}
        aria-label="Room render preview"
      >
        <div className="new-design-workspace-preview-content floorplan-room-render-preview-content">
          <div className="new-design-control-group floorplan-room-render-aspect-group">
            <div className="new-design-crop-mode-row" role="group" aria-label="Output aspect ratio">
              {CROP_MODE_KEYS.map((key) => {
                const CropModeIcon = CROP_MODE_ICONS[key];
                return (
                  <button
                    key={key}
                    type="button"
                    className={`new-design-chip new-design-crop-mode-btn${
                      selectedAspectKey === key ? ' new-design-chip--selected' : ''
                    }`}
                    aria-pressed={selectedAspectKey === key}
                    aria-label={CROP_MODES[key].label}
                    title={CROP_MODES[key].label}
                    onClick={() => onAspectRatioChange(CROP_MODES[key].openAiSize)}
                  >
                    <CropModeIcon size={17} strokeWidth={2} aria-hidden="true" />
                  </button>
                );
              })}
            </div>
          </div>

          <div className="camera-preview-section floorplan-room-render-preview-wrapper">
            <div ref={viewportRef} className="camera-preview-viewport floorplan-room-render-preview-viewport">
              {selectedRoomAnalysisJson ? (
                <SelectedAreaThreePreview
                  analysisJson={selectedRoomAnalysisJson}
                  imageWidth={canvasWidth}
                  imageHeight={canvasHeight}
                  cameraPreset={activeCameraPreset}
                  cameraFov={fov}
                  cameraControlMode="lookOnly"
                  hideDevControls
                  onCameraStateChange={onCameraStateChange}
                  joystickInputRef={joystickInputRef}
                  roomMoveBounds={roomMoveBounds}
                  onRegisterCaptureActions={onRegisterCaptureActions}
                  onCameraLookDrag={() => setShowRotateHint(false)}
                />
              ) : (
                <p className="new-2d-project-hint">Select a room to preview it in 3D.</p>
              )}
              {selectedRoomAnalysisJson && showRotateHint ? (
                <div className="camera-rotate-hint" aria-hidden="true">
                  <Rotate3D />
                  <span>Drag to look around</span>
                </div>
              ) : null}
              <RoomRenderCaptureOverlay
                captureRect={captureRect}
                viewportWidth={viewportSize.width}
                viewportHeight={viewportSize.height}
              />
            </div>
          </div>

          <div className="camera-controls-section floorplan-room-render-viewport-controls">
            <div className="camera-fov-control">
              <div className="camera-fov-title">Field Of View</div>
              <div className="camera-fov-slider-row">
                <span className="camera-fov-range-label">{MIN_FOV}°</span>
                <StyledRangeSlider
                  id="floorplan-room-render-fov"
                  min={MIN_FOV}
                  max={MAX_FOV}
                  value={fov}
                  onChange={(event) => onFovChange(Number(event.target.value))}
                  aria-label="Field of view"
                />
                <span className="camera-fov-range-label">{MAX_FOV}°</span>
              </div>
              <div className="camera-fov-current" aria-live="polite">
                Current {fov}°
              </div>
            </div>

            <div className="camera-movement-row floorplan-room-render-camera-control-row" role="group" aria-label="Camera controls">
              <div className="camera-switch-control floorplan-room-render-camera-switch">
                <button
                  type="button"
                  className="floor-plan-designer-tool-icon-btn floorplan-room-render-circle-btn"
                  onClick={handlePrevView}
                  disabled={cameraPresets.length < 2}
                  aria-label="Previous camera view"
                >
                  <ArrowLeft size={17} aria-hidden />
                </button>
                <span className="floorplan-room-render-camera-switch-label">Previous</span>
              </div>

              <AnalogJoystick
                onInputChange={handleJoystickInput}
                disabled={!joystickEnabled}
                aria-label="Move camera"
              />

              <div className="camera-switch-control floorplan-room-render-camera-switch floorplan-room-render-camera-switch--next">
                <button
                  type="button"
                  className="floor-plan-designer-tool-icon-btn floorplan-room-render-circle-btn"
                  onClick={handleNextView}
                  disabled={cameraPresets.length < 2}
                  aria-label="Next camera view"
                >
                  <ArrowRight size={17} aria-hidden />
                </button>
                <span className="floorplan-room-render-camera-switch-label">Next</span>
              </div>
            </div>
          </div>
        </div>
        {generating ? <GenerationLoadingState /> : null}
      </div>
    </section>
  );
}

export default RoomRenderSetupLayout;

